
// UTM Parameter tracking utilities

export interface UTMParams {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_term?: string;
  utm_content?: string;
  gclid?: string; // Google Click ID
  fbclid?: string; // Facebook Click ID
}

// Extract UTM parameters from URL
export const getUTMParams = (): UTMParams => {
  if (typeof window === 'undefined') {
    return {};
  }

  const urlParams = new URLSearchParams(window.location.search);
  
  const utmParams: UTMParams = {};
  
  // Extract UTM parameters
  const utmKeys: (keyof UTMParams)[] = [
    'utm_source',
    'utm_medium', 
    'utm_campaign',
    'utm_term',
    'utm_content',
    'gclid',
    'fbclid'
  ];

  utmKeys.forEach(key => {
    const value = urlParams.get(key);
    if (value) {
      utmParams[key] = value;
    }
  });

  return utmParams;
};

// Store UTM parameters in localStorage for session persistence
export const storeUTMParams = (utmParams?: UTMParams): void => {
  if (typeof window === 'undefined') {
    return;
  }

  const params = utmParams || getUTMParams();
  
  if (Object.keys(params).length > 0) {
    localStorage.setItem('taxhealth_utm_params', JSON.stringify(params));
    console.log('UTM parameters stored:', params);
  }
};

// Retrieve stored UTM parameters
export const getStoredUTMParams = (): UTMParams => {
  if (typeof window === 'undefined') {
    return {};
  }

  try {
    const stored = localStorage.getItem('taxhealth_utm_params');
    return stored ? JSON.parse(stored) : {};
  } catch (error) {
    console.error('Error retrieving stored UTM params:', error);
    return {};
  }
};

// Get UTM parameters (current URL or stored) as Record<string, string> for tracking
export const getCurrentUTMParams = (): Record<string, string> => {
  const currentParams = getUTMParams();
  const storedParams = getStoredUTMParams();
  
  // Current params take precedence over stored ones
  const combined = { ...storedParams, ...currentParams };
  
  // Filter out undefined values and ensure all values are strings
  const filtered: Record<string, string> = {};
  Object.entries(combined).forEach(([key, value]) => {
    if (value !== undefined) {
      filtered[key] = value;
    }
  });
  
  return filtered;
};

// Initialize UTM tracking (call on app load)
export const initUTMTracking = (): void => {
  if (typeof window === 'undefined') {
    return;
  }

  // Extract UTMs from current URL
  const utmParams = getUTMParams();

  if (Object.keys(utmParams).length > 0) {
    // Overwrite stored UTMs with current session's params
    storeUTMParams(utmParams);
  } else {
    // No UTMs in URL — clear old ones to avoid cross-session contamination
    localStorage.removeItem('taxhealth_utm_params');
  }

  // Always overwrite referrer (remove old "only if empty" guard)
  if (document.referrer) {
    localStorage.setItem('taxhealth_referrer', document.referrer);
    console.log('Referrer stored:', document.referrer);
  } else {
    localStorage.removeItem('taxhealth_referrer');
  }

  console.log('UTM tracking initialized with params:', getCurrentUTMParams());
};
